import React, {
  SetStateAction,
  Dispatch,
  Fragment,
  useEffect,
  useState,
  useRef,
} from 'react';
import styled from 'styled-components';

import {
  Reno,
  Denver,
  Fontana,
  Chicago,
  Atlanta,
  NewYork,
  Phoenix,
  Columbus,
  Stockton,
  LasVegas,
  PathsReno,
  Greenville,
  PathsDenver,
  PathsChicago,
  MapBaseLayer,
  SaltLakeCity,
  PathsFontana,
  Philadelphia,
  PathsColumbus,
  PathsGreenville,
} from '@components/atoms';
import { Hub } from '@shared/enums/hub';
import useDevice from '@hooks/useDevice';
import PathsStockton from '@components/atoms/PathsStockton';
import { drag, LastPosition, zoomIn, zoomOut } from '@utils/map';
import { DESKTOP, MOBILE, TABLET } from '@shared/constants/viewbox';
import { useSafariDragFix } from '@hooks/useSafariDragFix';
import { useScrollLock } from '@hooks/useScrollLock';
import Minus from '@public/icons/minus.svg';
import Plus from '@public/icons/plus.svg';

type InteractiveMapProps = {
  hub: Hub;
  setHub: Dispatch<SetStateAction<Hub>>;
};

function InteractiveMap({ hub, setHub }: InteractiveMapProps) {
  const { isMobile, isTablet } = useDevice();
  const svgRef = useRef<SVGSVGElement>(null);

  const [viewBox, setViewBox] = useState(DESKTOP);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosition, setLastPosition] = useState<LastPosition>(null);

  const isLasVegasVisible = false;
  const isSaltLakeCityVisible = hub !== Hub.Fontana;
  const isPhoenixVisible =
    hub !== Hub.Denver && hub !== Hub.Stockton && hub !== Hub.Fontana;

  useScrollLock({ isOpen: isDragging });
  useSafariDragFix(svgRef);

  useEffect(() => {
    if (isMobile) {
      return setViewBox(MOBILE);
    } else if (isTablet) {
      return setViewBox(TABLET);
    } else setViewBox(DESKTOP);
  }, [isMobile, isTablet]);

  const startDrag = (x: number, y: number) => {
    if (isMobile || isTablet) {
      setIsDragging(true);
      setLastPosition({ x, y });
    }
  };

  const handleMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    startDrag(event.clientX, event.clientY);
  };

  const handleTouchStart = (event: React.TouchEvent<SVGSVGElement>) => {
    const touch = event.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    drag(
      event.clientX,
      event.clientY,
      viewBox,
      lastPosition,
      setViewBox,
      isDragging,
      setLastPosition
    );
  };

  const handleTouchMove = (event: React.TouchEvent<SVGSVGElement>) => {
    const touch = event.touches[0];
    drag(
      touch.clientX,
      touch.clientY,
      viewBox,
      lastPosition,
      setViewBox,
      isDragging,
      setLastPosition
    );
  };

  const stopDrag = () => {
    setIsDragging(false);
    setLastPosition(null);
  };

  const onZoomIn = () => {
    zoomIn(viewBox, setViewBox);
  };

  const onZoomOut = () => {
    zoomOut(viewBox, setViewBox);
  };

  return (
    <Fragment>
      {(isMobile || isTablet) && (
        <StyledButtonsContainer style={{ marginBottom: '10px' }}>
          <StyledButton onClick={onZoomOut}>
            <Plus />
          </StyledButton>

          <StyledButton onClick={onZoomIn}>
            <Minus />
          </StyledButton>
        </StyledButtonsContainer>
      )}

      <svg
        ref={svgRef}
        viewBox={viewBox}
        onMouseUp={stopDrag}
        onTouchEnd={stopDrag}
        onMouseLeave={stopDrag}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        fill="none"
        width="864"
        height="542px"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <MapBaseLayer />

        {hub === Hub.Reno && <PathsReno />}
        {hub === Hub.Chicago && <PathsChicago />}
        {hub === Hub.Columbus && <PathsColumbus />}
        {hub === Hub.Greenville && <PathsGreenville />}
        {hub === Hub.Stockton && <PathsStockton />}
        {hub === Hub.Fontana && <PathsFontana />}
        {hub === Hub.Denver && <PathsDenver />}

        {isSaltLakeCityVisible && <SaltLakeCity />}
        {isLasVegasVisible && <LasVegas />}
        {isPhoenixVisible && <Phoenix />}
        <Philadelphia />
        <NewYork />
        <Atlanta />

        <Greenville hub={hub} setHub={setHub} />
        <Columbus hub={hub} setHub={setHub} />
        <Chicago hub={hub} setHub={setHub} />
        <Reno hub={hub} setHub={setHub} />
        <Denver hub={hub} setHub={setHub} />
        <Fontana hub={hub} setHub={setHub} />
        <Stockton hub={hub} setHub={setHub} />
      </svg>
    </Fragment>
  );
}

const StyledButtonsContainer = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  bottom: 5px;
  right: 15px;
`;

const StyledButton = styled.div`
  height: 40px;
  width: 40px;
  margin: 2px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: 0 1px 5px ${({ theme }) => theme.colors.cardBorder};
  background-color: ${({ theme }) => theme.colors.white};
`;

export default InteractiveMap;
